/* eslint-disable @typescript-eslint/no-explicit-any */

// Redacts tokens from strings and replaces with a placeholder string
const redactTokens = (function () {
  /**
   * Matches multi-part tokens that are separated by ":", "-", and "_"
   * but avoids matching trailing line numbers in call stacks Ex: ":500"
   */
  const LEGACY_PAYMENT_TOKEN_REDACTION_REGEX =
    /\/(p|pay|payments|d|deposit|transfers|verify|receipts|crypto\/order)\/[\w$-]*(?::[\w-]*[a-zA-Z][\w-]*)*(\/.*)?/g

  /**
   * Matches universal receipt paths: `/receipt/<type>/<token>` and redacts the token but preserves the type.
   */
  const RECEIPT_TOKEN_REDACTION_REGEX = /\/(receipt)\/([A-Z,_,-]+)\/([\w$:=+-]+)?/gi

  /**
   * Matches /f/anythingupto%3Fasdfhasdf
   * Capture group 1:'/f/'
   * Capture group 2: 'anythingupto'
   * Redacted: everything including and after the first occurrence of '%3F'
   */
  const FLOW_QUERY_PARAM_REDACTION_REGEX = /(\/f\/)(.+?(?=%3F))([^"]+)/gi

  /**
   * VERIFY_EMAIL_REGEX:
   * Matches URLs that include '/app/verify-email/' followed by an alphanumeric string, which is the
   * verification token.
   * Capture group 1: The path 'app/verify-email'
   * Capture group 2: The alphanumeric verification code or token that follows the verification
   * path. This code is case-sensitive and can include digits (0-9) and uppercase letters (A-Z).
   * Usage: Used to removed email verification tokens from 'app/verify-email' URLs
   */
  const VERIFY_EMAIL_REGEX = /\/(app\/verify-email)\/([0-9A-Z]+)/gi

  /**
   * APP_REWARD_REGEX:
   * Matches URLs that start with either '/app/' or '/reward/' followed by one or more word
   * characters, excluding those specifically leading to the '/verify-email' path.
   * Capture group 1: The initial part of the path, either 'app/' or 'reward/'
   * Capture group 2: Optionally captures the remainder of the URL path that follows the initial
   * word characters
   * The negative lookahead (?!verify-email) ensures that this part of the URL is explicitly excluded
   * Usage: Used to redact app and reward codes from URLs
   */
  const APP_REWARD_REGEX = /\/(app\/|reward\/)(?!verify-email)\w+(\/.*)?/g

  /**
   * CASHTAGS_REGEX:
   * Matches URLs containing unencoded currency symbols followed by alphanumeric characters, and
   * then a separator (/, :, whitespace, or end of line).
   * Capture group 1: The currency symbol ('$', '€', '£').
   * Capture group 2: The separator (/, :, whitespace, or end of line).
   * Usage: Insert 'CASHTAG' directly after the currency symbol and before the captured separator.
   */
  const CASHTAGS_REGEX = /\/(\$|€|£)\w+(\/|:|\s|$)/g

  /**
   * CASHTAGS_ENCODED_REGEX:
   * Matches URLs containing encoded currency symbols followed by alphanumeric characters, and then
   * a separator (encoded '/', :, whitespace, or end of line).
   * Capture group 1: Captures the encoded slash ('%2F') at the start.
   * Capture group 2: The encoded currency symbol ('%24' for '$', '%C2%A3' for '£', '%E2%82%AC' for '€').
   * Capture group 3: The separator (encoded '/', :, whitespace, or end of line).
   * Usage: Insert 'CASHTAG' directly after the encoded currency symbol and before the captured separator.
   * If an encoded slash is captured at the start, it is preserved.
   */
  const CASHTAGS_ENCODED_REGEX = /(%2F|\/)(%24|%C2%A3|%E2%82%AC)\w+(%2F|:|\s|$)/gi

  /**
   * CASHTAGS_BITCOIN_REGEX:
   * Matches URLs with unencoded currency symbols ('$','€','£'), followed by any characters leading
   * to '/bitcoin/' and then a sequence of digits.
   * Capture group 1: The currency symbol ('$','€','£').
   * Capture group 2: The '/bitcoin/' path segment.
   * Usage: Replace matched URLs to include 'CASHTAG' directly after the currency symbol and modify
   * the path to '/bitcoin/REDACTED', redacting the bitcoin invoice display id (legacy ids are
   * alphanumeric, Cash Lite ids carry a 'CL-' prefix). The cashtag match is '\w+' — immediately
   * followed by '/bitcoin/', matching the real route shape — so a single match cannot span from one
   * URL to another and collapse the text between them.
   */
  const CASHTAGS_BITCOIN_REGEX = /\/(\$|€|£)\w+(\/bitcoin\/)[\w-]+/g

  /**
   * CASHTAGS_ENCODED_BITCOIN_INVOICE_REGEX:
   * Matches URLs with encoded currency symbols ('%24' for '$', '%C2%A3' for '£', '%E2%82%AC' for '€'),
   * preceded by an encoded slash ('%2F'), followed by any characters leading to '/bitcoin/'
   * and then a sequence of digits.
   * Capture group 1: Captures the encoded slash ('%2F') at the start.
   * Capture group 2: The encoded currency symbol ('%24','%C2%A3','%E2%82%AC').
   * Capture group 3: The 'bitcoin' path segment with its surrounding separators, which may be
   * literal slashes or encoded ('%2F') — fully URL-encoded invoice links appear inside query
   * parameter values such as deep_link_value.
   * Usage: Replace matched URLs to include 'CASHTAG' directly after the encoded currency symbol
   * and 'REDACTED' after the preserved bitcoin segment, redacting the display id (legacy ids are
   * alphanumeric, Cash Lite ids carry a 'CL-' prefix). The cashtag match is '\w+' — immediately
   * followed by the bitcoin segment, matching the real route shape — so a single match cannot span
   * from one URL to another and collapse the text between them.
   */
  const CASHTAGS_ENCODED_BITCOIN_INVOICE_REGEX =
    /(%2F|\/)(%24|%C2%A3|%E2%82%AC)\w+((?:%2F|\/)bitcoin(?:%2F|\/))[\w-]+/gi

  /**
   * LAUNCH_BITCOIN_INVOICE_REGEX:
   * Matches bitcoin invoice launch deep links, where the cashtag comes after the '/bitcoin/'
   * segment: '/launch/bitcoin/{cashtag}/{displayId}' and '/launch/global/bitcoin/{cashtag}/{displayId}'.
   * The cashtag segment itself is redacted by the CASHTAGS regexes above; this regex redacts the
   * trailing display id, which those regexes cannot reach on launch paths.
   * Capture group 1: The path through the cashtag segment, up to and including its trailing slash.
   * Usage: Replace the display id with 'REDACTED', preserving the launch path and cashtag segment.
   */
  const LAUNCH_BITCOIN_INVOICE_REGEX = /(\/launch\/(?:global\/)?bitcoin\/[^/\s]+\/)[\w-]+/g

  /**
   * LAUNCH_BITCOIN_INVOICE_ENCODED_REGEX:
   * Matches URL-encoded bitcoin invoice launch deep links, as they appear inside query parameter
   * values (e.g. deep_link_value=https%3A%2F%2Fcash.app%2Flaunch%2Fglobal%2Fbitcoin%2F...).
   * The encoded cashtag segment is redacted by CASHTAGS_ENCODED_REGEX above; this regex redacts
   * the trailing display id, which that regex cannot reach on launch paths.
   * Capture group 1: The encoded path through the cashtag segment, up to and including its
   * trailing encoded slash ('%2F').
   * Usage: Replace the display id with 'REDACTED', preserving the launch path and cashtag segment.
   */
  const LAUNCH_BITCOIN_INVOICE_ENCODED_REGEX = /(%2Flaunch%2F(?:global%2F)?bitcoin%2F[^\s&"']+?%2F)[\w-]+/gi

  /**
   * NONCE_REGEX:
   * Matches strings that start with 'nonce-' followed by a base64 encoded value, which can include
   * characters A-Z, a-z, 0-9, plus (+), slash (/), and equals (=).
   * Usage: Used for redacting nonce values from script tags.
   */
  const NONCE_REGEX = /nonce-[A-Za-z0-9+/=]+/gi

  /**
   * FLOW_DATA_REGEX:
   * Matches flow data URLs containing grant-flow paths with brand prefixes and base64 encoded data.
   * Supports both direct grant-flow paths and login paths.
   * Capture group 1: The path (e.g., '/grant-flow/' or '/grant-flow/login/')
   * Capture group 2: The 2-character brand prefix (AP, CA, NH)
   * Capture group 3: The base64 encoded data after the hyphen
   * Usage: Redacts the sensitive base64 encoded data while preserving the brand prefix and path structure.
   */
  const FLOW_DATA_REGEX = /(\/(?:account\/)?grant-flow\/(?:login\/)?)([A-Z]{2})-([A-Za-z0-9+/=]+)/g

  /**
   * CUSTOMER_REQUEST_ID_QUERY_REGEX:
   * Matches a Checkout V2 customerRequestId query value GRR_x-<validity token> in a plain ('=')
   * query, where the separator is a literal '-' or its percent escape %2D, and the token consumes
   * percent escapes (for example %2B) and stops at '&', '#', '"', or whitespace.
   * Capture group 1: The parameter name, separator, and bare request ID.
   * Usage: Strips the validity token while keeping the bare request ID.
   */
  const CUSTOMER_REQUEST_ID_QUERY_REGEX = /(customerRequestId=[A-Za-z0-9_]+)(?:-|%2D)(?:%[0-9A-Fa-f]{2}|[^&#%"\s])+/gi

  /**
   * ENCODED_CUSTOMER_REQUEST_ID_QUERY_REGEX:
   * Matches a URL-encoded customerRequestId value ('%3D') inside return_to. The separator is a
   * literal '-' or the double-encoded hyphen %252D. The token consumes percent escapes except the
   * encoded delimiters %26 and %23, which end that level's value.
   * Capture group 1: The parameter name, separator, and bare request ID.
   * Usage: Strips the validity token while keeping the bare request ID.
   */
  const ENCODED_CUSTOMER_REQUEST_ID_QUERY_REGEX =
    /(customerRequestId%3D[A-Za-z0-9_]+)(?:-|%252D)(?:%(?!26|23)[0-9A-Fa-f]{2}|[^&#%"\s])+/gi

  /**
   * FLOW_PARAMS_REDACTION_REGEX:
   * Matches flow URLs with actual query parameters (not URL-encoded) where 'params' contains base64 data.
   * Matches: /f/SCENARIO?params=base64data&other=value or /f/SCENARIO?other=value&params=base64data
   * Capture group 1: Everything before params=
   * Capture group 2: The value of params parameter
   * Capture group 3: Everything after (& and remaining params, or end)
   * Usage: Redacts the base64-encoded params value that may contain PII
   */
  const FLOW_PARAMS_REDACTION_REGEX = /(\?|&)(params=)([^&]+)/gi
  const GENIE_PREVIEW_QUERY_REDACTION_REGEX = /(genie_preview(?:=|%3D))(?:%(?!26|23)[0-9A-Fa-f]{2}|[^&#%"\s])+/gi
  const GENIE_PREVIEW_TOKEN_REDACTION_REGEX = /GNS_[A-Za-z0-9]+/g

  /**
   * CASHTAGS_FULFILLMENT_REGEX:
   * Matches cashtag URLs with fulfillment types to normalize fulfillment type.
   * Matches: /$CASHTAG/pickup, /$CASHTAG/delivery, or /$CASHTAG/shipping
   * Capture group 1: The cashtag portion ($CASHTAG)
   * Usage: Normalizes fulfillment type to FULFILLMENT_TYPE
   */
  const CASHTAGS_FULFILLMENT_REGEX = /\/(\$\w+)\/(pickup|delivery|shipping)/g

  /**
   * CASHTAGS_LOCATION_REGEX:
   * Matches cashtag URLs with location tokens to normalize location token.
   * Matches: /$CASHTAG/l/CALL_TOKEN
   * Capture group 1: The cashtag and location path ($CASHTAG/l)
   * Usage: Normalizes location token to LOCATION_TOKEN
   */
  const CASHTAGS_LOCATION_REGEX = /\/(\$\w+\/l)\/[\w]+/g

  /**
   * CASHTAGS_LOCATION_FULFILLMENT_REGEX:
   * Matches cashtag URLs with both location tokens and fulfillment types.
   * Matches: /$CASHTAG/l/CALL_TOKEN/pickup, /$CASHTAG/l/CALL_TOKEN/delivery, or /$CASHTAG/l/CALL_TOKEN/shipping
   * Capture group 1: The cashtag and location path ($CASHTAG/l)
   * Usage: Normalizes both location token and fulfillment type
   */
  const CASHTAGS_LOCATION_FULFILLMENT_REGEX = /\/(\$\w+\/l)\/[\w]+\/(pickup|delivery|shipping)/g

  /**
   * CASHTAGS_LOCATION_IN_STORE_REGEX:
   * Matches cashtag URLs with location tokens and in-store tokens.
   * Matches: /$CASHTAG/l/CALL_TOKEN/in-store/IN_STORE_TOKEN
   * Capture group 1: The cashtag and location path ($CASHTAG/l)
   * Usage: Normalizes both location token and in-store token
   */
  const CASHTAGS_LOCATION_IN_STORE_REGEX = /\/(\$\w+\/l)\/[\w]+\/in-store\/[\w]+/g

  /**
   * CASHTAGS_ITEM_REGEX:
   * Matches brand-anchored item URLs to normalize item token. Only the brand anchor carries an
   * item address, so `item` in a location URL's fulfillment slot is left alone.
   * Matches: /$CASHTAG/item/ITEM_TOKEN
   * Capture group 1: The cashtag and item path ($CASHTAG/item)
   * Usage: Normalizes item token to ITEM_TOKEN
   */
  const CASHTAGS_ITEM_REGEX = /\/(\$\w+\/item)\/[\w-]+/g

  /**
   * CASHTAGS_CATEGORY_REGEX:
   * Matches Shop category page URLs to normalize category token. The all-categories overview
   * (/$CASHTAG/shop/categories) carries no token and is left alone.
   * Matches: /$CASHTAG/shop/category/CATEGORY_TOKEN
   * Capture group 1: The cashtag and category path ($CASHTAG/shop/category)
   * Usage: Normalizes category token to CATEGORY_TOKEN
   */
  const CASHTAGS_CATEGORY_REGEX = /\/(\$\w+\/shop\/category)\/[\w-]+/g

  /**
   * CASHTAGS_CHECKOUT_REGEX:
   * Matches checkout URLs to normalize cart token.
   * Matches: /$CASHTAG/checkout/CALCT_TOKEN (or any token)
   * Capture group 1: The cashtag and checkout path ($CASHTAG/checkout)
   * Usage: Normalizes cart token to CART_TOKEN
   */
  const CASHTAGS_CHECKOUT_REGEX = /\/(\$\w+\/checkout)\/[\w]+/g

  /**
   * CASHTAGS_CONFIRMATION_REGEX:
   * Matches confirmation URLs to normalize order token.
   * Matches: /$CASHTAG/confirmation/CALO_TOKEN (or any token)
   * Capture group 1: The cashtag and confirmation path ($CASHTAG/confirmation)
   * Usage: Normalizes order token to ORDER_TOKEN
   */
  const CASHTAGS_CONFIRMATION_REGEX = /\/(\$\w+\/confirmation)\/[\w]+/g

  return function (url: string, redaction = 'REDACTED') {
    return (
      url &&
      url
        .replace(LEGACY_PAYMENT_TOKEN_REDACTION_REGEX, `/$1/${redaction}$2`)
        .replace(RECEIPT_TOKEN_REDACTION_REGEX, `/$1/$2/${redaction}`)
        .replace(FLOW_QUERY_PARAM_REDACTION_REGEX, `$1$2?${redaction}`)
        .replace(FLOW_PARAMS_REDACTION_REGEX, `$1$2${redaction}`)
        .replace(GENIE_PREVIEW_QUERY_REDACTION_REGEX, `$1${redaction}`)
        .replace(GENIE_PREVIEW_TOKEN_REDACTION_REGEX, redaction)
        .replace(VERIFY_EMAIL_REGEX, '/$1/TOKEN')
        .replace(APP_REWARD_REGEX, '/$1REWARD_CODE$2')
        .replace(CASHTAGS_REGEX, '/$1CASHTAG$2')
        .replace(CASHTAGS_BITCOIN_REGEX, '/$1CASHTAG$2REDACTED')
        .replace(CASHTAGS_ENCODED_REGEX, '$1$2CASHTAG$3')
        .replace(CASHTAGS_ENCODED_BITCOIN_INVOICE_REGEX, '$1$2CASHTAG$3REDACTED')
        .replace(LAUNCH_BITCOIN_INVOICE_REGEX, '$1REDACTED')
        .replace(LAUNCH_BITCOIN_INVOICE_ENCODED_REGEX, '$1REDACTED')
        .replace(NONCE_REGEX, 'nonce-NONCEVALUE')
        .replace(FLOW_DATA_REGEX, `$1$2-${redaction}`)
        .replace(CUSTOMER_REQUEST_ID_QUERY_REGEX, '$1')
        .replace(ENCODED_CUSTOMER_REQUEST_ID_QUERY_REGEX, '$1')
        // Neighborhoods Profile Page routes
        .replace(CASHTAGS_FULFILLMENT_REGEX, '/$1/FULFILLMENT_TYPE')
        .replace(CASHTAGS_LOCATION_REGEX, '/$1/LOCATION_TOKEN')
        .replace(CASHTAGS_LOCATION_FULFILLMENT_REGEX, '/$1/LOCATION_TOKEN/FULFILLMENT_TYPE')
        .replace(CASHTAGS_LOCATION_IN_STORE_REGEX, '/$1/LOCATION_TOKEN/in-store/IN_STORE_TOKEN')
        .replace(CASHTAGS_ITEM_REGEX, '/$1/ITEM_TOKEN')
        .replace(CASHTAGS_CATEGORY_REGEX, '/$1/CATEGORY_TOKEN')
        // Neighborhoods Checkout routes
        .replace(CASHTAGS_CHECKOUT_REGEX, '/$1/CART_TOKEN')
        // Neighborhoods Confirmation routes
        .replace(CASHTAGS_CONFIRMATION_REGEX, '/$1/ORDER_TOKEN')
    )
  }
})()

// Recursively redacts tokens from any strings in the object and subobjects
// Modifies original object.
// Visits child arrays and objects.
const redactTokensRecursive = function (
  obj: Partial<{ [key: string]: any }> | null,
  /** @type {Array<any>}*/ visited: Array<any> = []
) {
  if (!obj) {
    return
  }
  visited.push(obj)

  Object.keys(obj).forEach(prop => {
    if (prop === 'file' && obj[prop] && obj[prop].indexOf('.js') > 0) {
      // Don't sanitize javascript file names like `/cash/app/activity.js:213123`
      return
    }
    const childObj = obj[prop]
    if (typeof childObj === 'string') {
      try {
        obj[prop] = redactTokens(childObj)
      } catch {
        /* Swallow error to prevent breaking the UI.
         * Adding a logger.info() creates a circlular reference.
         */
      }
    } else if ((Array.isArray(childObj) || typeof childObj === 'object') && !visited.includes(childObj)) {
      redactTokensRecursive(childObj, visited)
    }
  })
}

export { redactTokens, redactTokensRecursive }
